import { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useMatch, useParams } from 'react-router-dom';
import { RootState, useAppDispatch } from '../../store/store';
import { selectOffersByListingIdAndUserId } from '../../store/selectors/offers';
import { selectShowingsByListingIdAndUserId } from '../../store/selectors/showings';
import { selectInspectionsByListingIdAndUserId } from '../../store/selectors/inspections';
import { selectMessagesByListingIdAndUserId } from '../../store/selectors/messages';
import {
  Card,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  Dialog,
  Chip,
  Button,
  Box,
} from '@mui/material';
import {
  fetchMessages,
  messagesWithSenderAndReceiver,
} from '../../store/slices/messages';
import { sortBy, startCase } from 'lodash';
import Messages from './Messages';
import Showing from './Showing';
import Offer from './Offer/Offer';
import { OpenInFull } from '@mui/icons-material';
import { fetchOffers } from '../../store/slices/offers';
import listingsService, {
  listingsWithUploads,
  listingsWithMlsListing,
} from '../../api/services/listings';
import { liveDom } from '../../utils/liveDom';
import offersService from '../../api/services/offers';
import { fetchShowings } from '../../store/slices/showings';
import _ from 'lodash';
import CloseIcon from '@mui/icons-material/Close';
import { useSocket } from '../../api/socket';
import { useAuth } from '../../contexts/AuthContext';
import ListingCard from '../../components/ListingCard/ListingCard';
import Grid2 from '@mui/material/Unstable_Grid2';
import BuyerDetailFiles from './Files/BuyerDetailFiles';
import {
  offers,
  OfferStatus,
  UserType,
  Prisma,
  users,
  inspections,
  showings,
} from '@prisma/client';
import OfferTimeline from './OfferTimeline';
import useIsMobile from '../../hooks/useIsMobile';
import useIsWaitlisted from '../../hooks/useIsWaitlisted';
import WaitlistNotice from '../../components/WaitlistNotice/WaitlistNotice';
import { fetchTransactions } from '../../store/slices/transactions';
import { fetchInspections } from '../../store/slices/inspections';
import NiceModal from '@ebay/nice-modal-react';
import ScheduleTourModal from '../Dashboard/Buying/ScheduleTourModal/ScheduleTourModal';
import CreateInspectionModal from '../../components/CreateInspectionModal/CreateInspectionModal';
import { InspectionStatus as IS } from '@prisma/client';
import PropertyStepFlow from '../../components/PropertyStepFlow/PropertyStepFlow';
import MLSListingImage from '../../components/MLSListingImage/MLSListingImage';
import MarketAnalysis from '../../components/MarketAnalysis/MarketAnalysis';
import PropertyFeedback from '../Dashboard/Buying/Properties/PropertyFeedback';
import PhotosModal from '../Listings/components/PhotosModal';
import TourWalkthroughPanel from '../TourWalkthrough/TourWalkthroughPanel';
import { usePageTitle } from '../../contexts/PageTitleContext';

// Type for public offer data - it's an offer with its relations
type PublicOfferData = Prisma.offersGetPayload<{
  include: {
    user: true;
    listings: true;
    offerRounds: true;
  };
}>;

// Unified details type for all views
type DetailsType = {
  buyer: users;
  user: users;
  offers: offers[];
  showings?: showings[];
  inspections: inspections[];
  messages: messagesWithSenderAndReceiver;
  listingId: string;
  hasPendingOffer: boolean;
};

// View type enum for cleaner logic
enum ViewType {
  BUYER = 'BUYER',
  SELLER = 'SELLER',
  PUBLIC = 'PUBLIC',
}

const Detail: React.FC = () => {
  const { id, listingId, token } = useParams<{
    id?: string;
    listingId?: string;
    token?: string;
  }>();
  const isAdminBuyerPreview =
    useMatch('/admin/buyers/:id/listing/:listingId') !== null;
  const dispatch = useAppDispatch();
  const { connect, socket } = useSocket();
  const { currentUser } = useAuth();
  const isMobile = useIsMobile();
  const setPageTitle = usePageTitle();

  // Determine view type
  const viewType = useMemo(() => {
    if (token) return ViewType.PUBLIC;
    if (isAdminBuyerPreview) return ViewType.BUYER;
    if (currentUser?.id === id) return ViewType.BUYER;
    return ViewType.SELLER;
  }, [token, isAdminBuyerPreview, currentUser?.id, id]);

  // Determine the target userId based on view type
  // BUYER view: current user's data
  // SELLER view: specific buyer's data (from id param)
  const targetUserId =
    viewType === ViewType.BUYER && !isAdminBuyerPreview
      ? currentUser?.id
      : id;

  // Get data using user-context-aware selectors
  const listingOffers = useSelector((state: RootState) =>
    listingId && targetUserId
      ? selectOffersByListingIdAndUserId(listingId, targetUserId)(state)
      : []
  );
  const listingShowings = useSelector((state: RootState) =>
    listingId && targetUserId
      ? selectShowingsByListingIdAndUserId(listingId, targetUserId)(state)
      : []
  );
  const listingInspections = useSelector((state: RootState) =>
    listingId && targetUserId
      ? selectInspectionsByListingIdAndUserId(listingId, targetUserId)(state)
      : []
  );
  const listingMessages = useSelector((state: RootState) =>
    listingId && targetUserId
      ? selectMessagesByListingIdAndUserId(listingId, targetUserId)(state)
      : []
  );

  // State
  const [listing, setListing] = useState<listingsWithUploads | null>(null);
  const [loading, setLoading] = useState(true);
  const [messagesFullscreen, setMessagesFullscreen] = useState(false);
  const [publicOfferData, setPublicOfferData] =
    useState<PublicOfferData | null>(null);
  const [_, setLastUpdated] = useState<Date>(new Date());
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    if (listing?.address) {
      setPageTitle(listing.address);
    }
  }, [listing?.address, setPageTitle]);

  // Build unified details object based on view type
  const details: DetailsType | null = useMemo(() => {
    if (viewType === ViewType.PUBLIC && publicOfferData) {
      // Transform publicOfferData to match the same structure as other views
      return {
        buyer: publicOfferData.user,
        user: publicOfferData.user,
        offers: [publicOfferData],
        showings: undefined,
        inspections: [],
        messages: [],
        listingId: publicOfferData.listingId,
        hasPendingOffer: false,
      };
    }

    if (listingId) {
      // For both BUYER and SELLER views, use the same generic data
      // Backend manages permissions - if data is returned, user can see it
      const buyer =
        listingOffers[0]?.user || listingShowings[0]?.user || currentUser;
      const hasPendingOffer = listingOffers.some((o) => o.status === 'REVIEW');

      return {
        buyer,
        user: buyer,
        offers: listingOffers,
        showings: listingShowings,
        inspections: listingInspections,
        messages: listingMessages,
        listingId,
        hasPendingOffer,
      };
    }

    return null;
  }, [
    viewType,
    publicOfferData,
    listingId,
    listingOffers,
    listingShowings,
    listingInspections,
    listingMessages,
    currentUser,
  ]);

  const isPbuyer = currentUser?.type === UserType.PBUYER;

  // Buyers drive the tour/inspection/offer workflow from the property step
  // flow, so the documents list defers to it while it owns signing.
  const showsBuyerStepFlow = viewType === ViewType.BUYER && !isPbuyer;

  // The step flow only offers "Review & Sign" at REVIEWED, and it covers the
  // purchase agreement and its exhibits. Documents generated later -- a
  // contract addendum after acceptance -- are reachable nowhere else, and
  // their DocuSeal submitters are created with send_email off, so the
  // documents list has to stay actionable for those.
  const stepFlowOwnsSigning =
    showsBuyerStepFlow && details?.offers?.[0]?.status === OfferStatus.REVIEWED;

  const fetchListing = async () => {
    const listingId = details?.listingId;
    if (listingId) {
      const listing = await listingsService.get({
        id: listingId,
      });
      setListing(listing);
    }
  };

  const fetchPublicOfferData = async () => {
    if (token) {
      try {
        // Decode the token to get the offer ID (JWT tokens have 3 parts separated by dots)
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          const offerId = payload.offerId;

          if (offerId) {
            // Use the offers service with token
            const data = await offersService.getWithToken(offerId, token);

            setPublicOfferData(data);
            setListing(data.listings);
            setLoading(false);
            setLastUpdated(new Date());
          } else {
            console.error('No offer ID found in token payload');
            setLoading(false);
          }
        } else {
          console.error('Invalid token format');
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
      }
    }
  };

  const fetchData = async () => {
    if (viewType !== ViewType.PUBLIC) {
      await Promise.all([
        dispatch(fetchMessages()),
        dispatch(fetchOffers()),
        dispatch(fetchShowings()),
        dispatch(fetchTransactions()),
        dispatch(fetchInspections()),
      ]);
    }
  };

  // Main data fetching effect
  useEffect(() => {
    const initialize = async () => {
      if (viewType === ViewType.PUBLIC) {
        await fetchPublicOfferData();
      } else {
        await fetchData();
      }
    };
    initialize();
  }, [viewType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch listing when details are available
  useEffect(() => {
    const initialize = async () => {
      if (details?.listingId && !listing) {
        await fetchListing();
        setLoading(false);
      }
    };
    initialize();
  }, [details?.listingId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Socket connection for messages
  useEffect(() => {
    connect();
    dispatch(fetchMessages());

    const handleNewMessages = () => {
      dispatch(fetchMessages());
    };

    socket.on('messages created', handleNewMessages);

    return () => {
      socket.off('messages created', handleNewMessages);
    };
  }, [connect, socket, dispatch]);

  // Refresh data when page becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Refresh data based on view type
        if (viewType === ViewType.PUBLIC) {
          // For public/offer portal, only refresh offer data
          fetchPublicOfferData();
        } else if (viewType === ViewType.BUYER) {
          // For buyers, refresh offers and messages
          dispatch(fetchOffers());
          dispatch(fetchMessages());
        } else if (viewType === ViewType.SELLER) {
          // For sellers, refresh offers only
          dispatch(fetchOffers());
        }
      }
    };

    // Add event listener for visibility change
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [viewType, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Add polling for public view to get real-time updates
  useEffect(() => {
    let pollInterval: NodeJS.Timeout | undefined;

    if (viewType === ViewType.PUBLIC && token) {
      // Poll every 120 seconds for updates
      pollInterval = setInterval(() => {
        fetchPublicOfferData();
      }, 120000);
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [viewType, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get data from the appropriate source
  const displayData = details;
  const messages = sortBy(displayData?.messages, 'createdAt');

  // Calculate derived values before any conditional returns
  const offersList = displayData?.offers || [];
  const multipleOffers = offersList.length > 1;
  const acceptedOffer = offersList.find(
    (offer: offers) => offer?.status === OfferStatus.ACCEPTED
  );
  const hasAcceptedOffer = !!acceptedOffer;

  const messageText = useMemo(() => {
    if (isPbuyer)
      return 'These are messages between you and the home seller. Homepoint can see these messages.';
    if (viewType === ViewType.PUBLIC)
      return 'These are messages between you and the home buyer.';
    if (viewType === ViewType.SELLER)
      return 'These are messages between you and the home buyer. Homepoint can see these messages.';
    return 'These are messages between you and the home buyer.';
  }, [isPbuyer, viewType]);

  // Loading state - after all hooks
  if (loading || !listing) {
    return (
      <Grid2 container spacing={2}>
        <Grid2 xs={12}>
          <Card sx={{ pl: 2, pr: 2 }}>
            <Skeleton height={150} />
          </Card>
        </Grid2>
        <Grid2 xs={12}>
          <Card sx={{ pl: 2, pr: 2 }}>
            <Skeleton height={150} />
          </Card>
        </Grid2>
        <Grid2 xs={12}>
          <Card sx={{ pl: 2, pr: 2 }}>
            <Skeleton height={150} />
          </Card>
        </Grid2>
      </Grid2>
    );
  }

  return (
    <Grid2 container spacing={2}>
      {(isPbuyer ||
        viewType === ViewType.PUBLIC ||
        viewType === ViewType.BUYER) && (
        <Grid2
          xs={12}
          md={showsBuyerStepFlow ? 12 : 7}
          lg={showsBuyerStepFlow ? 5 : 7}
        >
          <Card
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              '& > *': { width: '100%' },
            }}
          >
            {listing.type === 'BUYER' ? (
              <BuyerListingInfo listing={listing} />
            ) : (
              <ListingCard
                listing={listing}
                slim={true}
                showViewButton={isPbuyer}
                showEditButton={false}
                showAdjustPriceButton={false}
              />
            )}
          </Card>
        </Grid2>
      )}
      {showsBuyerStepFlow && details && listing && (
        <Grid2 xs={12} lg={7}>
          <Card sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Your purchase journey
            </Typography>
            <PropertyStepFlow
              layout="rows"
              listing={listing}
              showings={details.showings}
              inspections={details.inspections || []}
              offer={details.offers?.[0]}
              onUpdate={() => {
                dispatch(fetchShowings());
                dispatch(fetchInspections());
                dispatch(fetchOffers());
              }}
              onLeaveFeedback={() => setFeedbackOpen(true)}
              readOnly={isAdminBuyerPreview}
            />
          </Card>
        </Grid2>
      )}
      {/* The buyer's pricing read. Sellers get the price opinion tools instead,
          and PBUYER accounts are the counterparty on a listing -- neither sees a
          "you have room to negotiate" verdict on inventory that is theirs. */}
      {showsBuyerStepFlow && listing && (
        <Grid2 xs={12} id="reva-offer-plan">
          <MarketAnalysis
            listing={listing}
            readOnly={isAdminBuyerPreview}
            hasPendingOffer={details?.hasPendingOffer}
          />
        </Grid2>
      )}
      {viewType !== ViewType.PUBLIC &&
        displayData?.showings &&
        displayData.showings.length > 0 &&
        (isPbuyer || viewType !== ViewType.BUYER) && (
          <Grid2 xs={12}>
            <Card sx={{ padding: 2 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="h6">Showing</Typography>
                <Chip
                  label={
                    <Typography variant="body2" fontWeight={600}>
                      {startCase(displayData.showings[0].type.toLowerCase())}
                    </Typography>
                  }
                  color="primary"
                />
              </Stack>
              <Showing showing={displayData.showings[0]} />
            </Card>
          </Grid2>
        )}
      {viewType !== ViewType.PUBLIC &&
        displayData?.showings &&
        displayData.showings.length > 0 && (
          <Grid2 xs={12}>
            <TourWalkthroughPanel
              showingId={
                [...(displayData.showings || [])]
                  .filter((s) => s.type === 'TOUR')
                  .sort(
                    (a, b) =>
                      new Date(b.proposedTime).getTime() -
                      new Date(a.proposedTime).getTime()
                  )[0]?.id || displayData.showings[0].id
              }
            />
          </Grid2>
        )}
      {multipleOffers && (
        <Grid2 xs={12}>
          <OfferTimeline offers={offersList} />
        </Grid2>
      )}
      {(offersList.length > 0 || !showsBuyerStepFlow) && (
        <Grid2 xs={12}>
          <Card sx={{ padding: 2 }}>
            <Offer
              offers={offersList}
              listing={listing}
              isPublicView={viewType === ViewType.PUBLIC}
              token={token}
              showOfferCreation={isPbuyer}
              buyerPreview={isAdminBuyerPreview}
              readOnly={isAdminBuyerPreview}
            />
          </Card>
        </Grid2>
      )}
      {/* Before acceptance a buyer attaches offer documents in the offer create
          modal; a standalone upload area here led them to attach files outside
          it. Sellers and offer-portal viewers still need the list to review an
          offer before accepting. */}
      {(viewType !== ViewType.BUYER || acceptedOffer) && (
        <Grid2 xs={12}>
          <BuyerDetailFiles
            listing={listing}
            offerId={(acceptedOffer ?? offersList[0])?.id}
            token={token}
            offerStatus={(acceptedOffer ?? offersList[0])?.status}
            allowSigning={!stepFlowOwnsSigning && !isAdminBuyerPreview}
            readOnly={isAdminBuyerPreview}
          />
        </Grid2>
      )}
      {hasAcceptedOffer && viewType !== ViewType.PUBLIC && (
        <Grid2 xs={12}>
          <Card sx={{ padding: 2 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Typography variant="h6">Messages</Typography>
              <Typography variant="caption" color="text.secondary">
                {messageText}
              </Typography>
              {isMobile && (
                <IconButton onClick={() => setMessagesFullscreen(true)}>
                  <OpenInFull fontSize="small" />
                </IconButton>
              )}
            </Stack>
            <Messages
              messages={messages}
              listing={listing}
              buyerId={id || displayData?.user?.id || ''}
              fullscreen={false}
              readOnly={isAdminBuyerPreview}
            />
          </Card>
        </Grid2>
      )}

      <Dialog
        fullScreen
        open={messagesFullscreen}
        onClose={() => setMessagesFullscreen(false)}
      >
        <Stack sx={{ height: '100%', p: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Typography variant="h6">Messages</Typography>
            <IconButton onClick={() => setMessagesFullscreen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Messages
            messages={messages}
            listing={listing}
            buyerId={id || displayData?.user?.id || ''}
            fullscreen={true}
            readOnly={isAdminBuyerPreview}
          />
        </Stack>
      </Dialog>

      <Dialog
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <PropertyFeedback
          onSubmit={() => {
            setFeedbackOpen(false);
            dispatch(fetchShowings());
          }}
          listingId={listing?.id || ''}
          initialValue={
            listing
              ? {
                  pros: listing.pros || [],
                  cons: listing.cons || [],
                  feedback: listing.feedback || '',
                  overallFeedback: listing.overallFeedback || null,
                }
              : undefined
          }
        />
      </Dialog>
    </Grid2>
  );
};

// Buyer action buttons component
interface BuyerActionButtonsProps {
  listing: any;
  offers: any[];
  inspections: any[];
  showing?: any;
  hasPendingOffer: boolean;
}

const BuyerActionButtons: React.FC<BuyerActionButtonsProps> = ({
  listing,
  inspections,
  showing,
  hasPendingOffer,
}) => {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const isWaitlisted = useIsWaitlisted();
  const showInspectionButton = true;
  const hasInspections = inspections.length > 0;
  const inspectionCompleted =
    hasInspections && inspections.every((i) => i.status === IS.COMPLETED);
  const inspectionScheduledNotComplete = hasInspections && !inspectionCompleted;

  const inspectionButtonText = inspectionCompleted
    ? 'View Inspections'
    : 'Schedule Inspections';

  if (isWaitlisted) {
    return (
      <Grid2 xs={12}>
        <Box sx={{ py: 2 }}>
          <WaitlistNotice variant="compact" />
        </Box>
      </Grid2>
    );
  }

  return (
    <Grid2 xs={12}>
      <Box sx={{ py: 2 }}>
        <Stack
          direction={isMobile ? 'column' : 'row'}
          spacing={2}
          sx={{
            '& > *': {
              width: isMobile ? '100%' : 'auto',
            },
          }}
        >
          <Button
            variant="contained"
            color="highlight"
            disabled={!!showing || hasPendingOffer}
            fullWidth={isMobile}
            onClick={() => {
              NiceModal.show(ScheduleTourModal, { listing, numDays: 5 });
            }}
          >
            Schedule Tour
          </Button>

          {showInspectionButton && (
            <Button
              variant="contained"
              color="secondary"
              sx={{ color: 'white' }}
              disabled={inspectionScheduledNotComplete}
              fullWidth={isMobile}
              onClick={() => {
                if (inspectionCompleted) {
                  const inspection = inspections?.find(
                    (i) => i.status === IS.COMPLETED
                  );
                  if (inspection?.reportUrl) {
                    window.open(inspection.reportUrl, '_blank');
                  }
                } else {
                  NiceModal.show(CreateInspectionModal, {
                    listing,
                    onSuccess: () => dispatch(fetchInspections()),
                  });
                }
              }}
            >
              {inspectionButtonText}
            </Button>
          )}
        </Stack>
      </Box>
    </Grid2>
  );
};

// Buyer listing info component - the single source of truth for this
// listing's facts (address, price, beds/baths/sqft/built/lot, DOM, listed
// date). MarketAnalysis renders its own pricing read directly below this on
// the buyer detail page, so it no longer repeats any of these facts.
// The buyer's pricing read lives in the MarketAnalysis card below this on the
// buyer detail page; PUBLIC and PBUYER views are the counterparty's and get no
// pricing read at all, so the header carries facts only.
const BuyerListingInfo: React.FC<{
  listing: listingsWithUploads & Partial<listingsWithMlsListing>;
}> = ({ listing }) => {
  const isMobile = useIsMobile();
  const displayPrice = listing.mlsListing?.listingPrice || listing.price || 0;
  const mlsListing = listing.mlsListing;
  const mlsPhotos = listing.mlsListing?.photos as string[] | undefined;

  const handlePhotoClick = () => {
    if (mlsPhotos && mlsPhotos.length > 0) {
      NiceModal.show(PhotosModal, {
        photos: mlsPhotos,
        initialPhotoIndex: 0,
      });
    }
  };

  const dom =
    mlsListing?.dom !== null && mlsListing?.dom !== undefined
      ? liveDom({
          dom: mlsListing.dom,
          dateListed: mlsListing.dateListed,
          isActive: mlsListing.isActive,
        })
      : null;

  // Compact facts row: "4 bd · 3 ba · 2,410 sqft · Built 1998 · 0.4 ac"
  const facts = [
    listing.beds !== undefined ? `${listing.beds} bd` : null,
    listing.baths !== undefined ? `${listing.baths} ba` : null,
    listing.area ? `${listing.area.toLocaleString()} sqft` : null,
    mlsListing?.yearBuilt ? `Built ${mlsListing.yearBuilt}` : null,
    mlsListing?.lotSizeAcres ? `${mlsListing.lotSizeAcres} ac` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const metaLine = [
    dom !== null ? `${dom} days on market` : null,
    mlsListing?.activationDate
      ? `Listed ${new Date(mlsListing.activationDate).toLocaleDateString()}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '96px minmax(0, 1fr)',
          sm: '128px minmax(0, 1fr)',
        },
        gap: 2,
        p: 2,
        alignItems: 'center',
      }}
    >
      <Box>
        <MLSListingImage
          listing={listing}
          width="100%"
          height={isMobile ? 112 : 144}
          onClick={handlePhotoClick}
        />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Stack spacing={0.75}>
          <Stack spacing={0.25}>
            <Typography variant="h6" fontWeight={600}>
              {listing.address}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {listing.city}, {listing.state} {listing.zip}
            </Typography>
          </Stack>

          {displayPrice > 0 && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
            >
              <Typography variant="h5" color="primary" fontWeight={700}>
                ${displayPrice.toLocaleString()}
              </Typography>
            </Stack>
          )}

          {facts && (
            <Typography
              variant="body2"
              color="text.secondary"
              data-testid="listing-facts-row"
            >
              {facts}
            </Typography>
          )}

          {metaLine && (
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid="listing-meta-line"
            >
              {metaLine}
            </Typography>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default Detail;
